import { call, put, takeLatest } from 'redux-saga/effects';
import { DownloadService } from '@src/utility/download.service';
import DOMPurify from 'dompurify';
import { formatDate } from '@src/apps/ContentEditor/Faq/FaqList/faq-list-methods';
import {
  FETCH_FAQ_CATEGORIES_REQUEST,
  SAVE_FAQ_FORM_REQUEST,
  FETCH_FAQ_LIST_DATA_REQUEST,
  DELETE_FAQ_REQUEST,
  EXPORT_FAQ_DATA_REQUEST,
  SET_FAQ_EDIT_DATA_REQUEST,
} from '@src/middleware/actions/actionTypes';

import {
  fetchFaqCategoriesSuccess,
  fetchFaqCategoriesFailure,
  fetchFaqCategoriesLoading,
  saveFaqFormDataSuccess,
  saveFaqFormDataFailure,
  saveFaqFormDataLoading,
  fetchFaqListDataSuccess,
  fetchFaqListDataFailure,
  fetchFaqListDataLoading,
  deleteFaqSuccess,
  deleteFaqFailure,
  deleteFaqLoading,
  exportFaqDataSuccess,
  exportFaqDataFailure,
  exportFaqDataLoading,
  setFaqEditDataSuccess,
  setFaqEditDataFailure,
  setFaqEditDataLoading,
} from '@src/middleware/actions/Faq/faqActions';

import {
  saveFaqFormData,
  deleteFaq,
  fetchFaqCategories, 
  fetchFaqListData,
  fetchSingleFaqData,
} from '../services/faq/faqService';

function* handleFetchFaqCategories() {
  try {
    yield put(fetchFaqCategoriesLoading(true));
    const response = yield call(fetchFaqCategories);
    yield put(fetchFaqCategoriesSuccess(response.Data));
  } catch (error) {
    yield put(fetchFaqCategoriesFailure(error.message));
  }
}
function* handleSaveFaqForm(action: any) {
  try {
    // Simulate API call to save FAQ form data
    yield put(saveFaqFormDataLoading(true));
    const response = yield call(saveFaqFormData, action.payload);
    if (response.StatusCode !== 200) {
      throw new Error(response.Message || 'Failed to save FAQ form data');
    }
    yield put(saveFaqFormDataSuccess(response.Data));
  } catch (error) {
    yield put(saveFaqFormDataFailure(error.message));
  }
}

function* handleFetchFaqListData() {
  try {
    yield put(fetchFaqListDataLoading(true));
    const response = yield call(fetchFaqListData);
    yield put(fetchFaqListDataSuccess(response.Data));
    yield put(fetchFaqListDataLoading(false));
  } catch (error) {
    yield put(fetchFaqListDataFailure(error.message));
    yield put(fetchFaqListDataLoading(false));
  }
}

function* handleDeleteFaq(action: any) {
  try {
    // Simulate API call to delete FAQ
    yield put(deleteFaqLoading());
    const response = yield call(deleteFaq, action.payload);
    if (response.StatusCode !== 200) {
      throw new Error(response.Message || 'Failed to delete FAQ');
    }
    yield put(deleteFaqSuccess());
  } catch (error) {
    yield put(deleteFaqFailure());
  }
}

function* handleExportFaqData(action: any) {
  try {
    // Simulate API call to export FAQ data
    const faqData = action.payload;
    yield put(exportFaqDataLoading());
    const sheets: Array<any> = [];
    const kCExporttitle = 'FAQ Documentation Manager';
    const formatedExportData = faqData.map((content, index) => {
      return {
        'Question Name': content.Question,
        Answer: DOMPurify.sanitize(content.Answer),
        'More Info': DOMPurify.sanitize(content.MoreInfo),
        Notes: DOMPurify.sanitize(content.Notes),
        'Available In': content.MetricAvailableIn,
        Category: content.CategorySource,
        'Last Updated': content.LastUpdated
          ? formatDate(content.LastUpdated)
          : '',
        'Last Edited By': content.LastEditedBy || '',
      };
    });
    sheets.push({ title: kCExporttitle, data: formatedExportData });
    DownloadService.download(
      { sheets },
      {
        fileFormat: '.xlsx',
        fileName: 'FAQ Documentation Manager',
        journeyName: 'Content Editor',
        metricName: 'FAQ Documentation Manager',
      }
    );
    yield put(exportFaqDataSuccess());
  } catch (error) {
    yield put(exportFaqDataFailure());
  }
}

function* handleSetFaqEditData(action: any) {
  try {
    yield put(setFaqEditDataLoading());
    const response = yield call(fetchSingleFaqData, action.payload);
    if (response.StatusCode !== 200) {
      throw new Error(response.Message || 'Failed to fetch single FAQ data');
    }
    if (response && response?.Data && response.Data.length > 0) {
      yield put(setFaqEditDataSuccess(response.Data[0]));
    } else {
      throw new Error('No FAQ data found for the given QuestionId');
    }
  } catch (error) {
    yield put(setFaqEditDataFailure(error.message));
  }
}

export function* faqSaga() {
  yield takeLatest(FETCH_FAQ_CATEGORIES_REQUEST, handleFetchFaqCategories);
  yield takeLatest(SAVE_FAQ_FORM_REQUEST, handleSaveFaqForm);
  yield takeLatest(FETCH_FAQ_LIST_DATA_REQUEST, handleFetchFaqListData);
  yield takeLatest(DELETE_FAQ_REQUEST, handleDeleteFaq);
  yield takeLatest(EXPORT_FAQ_DATA_REQUEST, handleExportFaqData);
  yield takeLatest(SET_FAQ_EDIT_DATA_REQUEST, handleSetFaqEditData);
}
